import Model from './BaseModel'
import i18n from '@/plugins/i18n'
import moment from 'moment'
import Customer from './Customer'
import Plan from './Plan'
import Bill from './Bill'
import Commission from './Commission'

export default class Period extends Model {
    resourceName() {
        return 'periods'
    }

    fields() {
        return [
            'id',
            'plan_id',
            'plan_name',
            'customer_name',
            'subscription_id',
            'customer_id',
            'external_key',
            'start_at',
            'end_at',
            'flg_charged',
            'value',
            'due_at',
            'paid_at',
            'canceled_at',
            'changed_at',
            'created_at',
            'updated_at',
            'deleted_at',
            'billable',
        ]
    }

    relationships() {
        return {
            customer: new Customer(),
            plan: new Plan(),
            bill: new Bill(),
            commission: new Commission(),
        }
    }

    dates() {
        return {
            start_at: 'YYYY-MM-DD',
            end_at: 'YYYY-MM-DD',
            due_at: 'YYYY-MM-DD',
            paid_at: 'YYYY-MM-DD',
            canceled_at: 'YYYY-MM-DD',
            changed_at: 'YYYY-MM-DD',
            created_at: 'YYYY-MM-DD',
            updated_at: 'YYYY-MM-DD',
            deleted_at: 'YYYY-MM-DD',
        }
    }

    computed() {
        return {
            formattedStartAt(period) {
                return period.start_at.isValid()
                    ? i18n.d(period.start_at, 'short')
                    : null
            },

            formattedEndAt(period) {
                return period.end_at.isValid()
                    ? i18n.d(period.end_at, 'short')
                    : null
            },

            formattedDueAt(period) {
                return period.due_at.isValid()
                    ? i18n.d(period.due_at, 'short')
                    : null
            },

            formattedPaidAt(period) {
                return period.paid_at.isValid()
                    ? i18n.d(period.paid_at, 'short')
                    : null
            },

            formattedCanceledAt(period) {
                return period.canceled_at.isValid()
                    ? i18n.d(period.canceled_at, 'datetime')
                    : null
            },

            formattedChangedAt(period) {
                return period.changed_at.isValid()
                    ? i18n.d(period.changed_at, 'datetime')
                    : null
            },

            formattedCreatedAt(period) {
                return period.created_at.isValid()
                    ? i18n.d(period.created_at, 'datetime')
                    : null
            },

            formattedUpdatedAt(period) {
                return period.updated_at.isValid()
                    ? i18n.d(period.updated_at, 'datetime')
                    : null
            },

            formattedDeletedAt(period) {
                return period.deleted_at.isValid()
                    ? i18n.d(period.deleted_at, 'datetime')
                    : null
            },

            canceled(period) {
                return period.canceled_at.isValid() ||
                    period.changed_at.isValid()
                    ? 'Cancelado'
                    : '-'
            },

            paidStatus(period) {
                if (period.paid_at.isValid()) {
                    return 'Pago'
                }

                if (
                    !period.paid_at.isValid() &&
                    period.due_at.isValid() &&
                    period.due_at >= moment().startOf('day')
                ) {
                    return 'Em aberto'
                }

                if (
                    !period.paid_at.isValid() &&
                    period.due_at.isValid() &&
                    period.due_at < moment().endOf('day')
                ) {
                    return 'Atrasado'
                }
            },

            paidColor(period) {
                if (period.paid_at.isValid()) {
                    return 'teal white--text'
                }

                if (
                    !period.paid_at.isValid() &&
                    period.due_at.isValid() &&
                    period.due_at >= moment().startOf('day')
                ) {
                    return 'yellow darken-3 white--text'
                }

                if (
                    !period.paid_at.isValid() &&
                    period.due_at.isValid() &&
                    period.due_at < moment().endOf('day')
                ) {
                    return 'red lighten-1 white--text'
                }
            },

            formattedValue(period) {
                return i18n.n(period.value, 'currency')
            },

            resellerPayment(period) {
                if (period.hasBill)
                    return (
                        i18n.tc('resources.bill.name', 1) +
                        ': ' +
                        period.bill.reference
                    )

                if (period.hasCommission)
                    return (
                        i18n.tc('resources.commission.name', 1) +
                        ': ' +
                        period.commission.month +
                        '/' +
                        period.commission.year
                    )
            },
        }
    }

    get tableColumns() {
        return [
            {
                text: i18n.t('resources.period.attributes.customer'),
                value: 'customer_name',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.plan'),
                value: 'plan_name',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.start_at'),
                value: 'formattedStartAt',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.end_at'),
                value: 'formattedEndAt',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.value'),
                value: 'formattedValue',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.paid_at'),
                value: 'formattedPaidAt',
                sortable: false,
            },
            {
                text: i18n.t('resources.period.attributes.reseller_payment'),
                value: 'resellerPayment',
                sortable: false,
            },
            {
                text: i18n.t('tables.actions'),
                value: 'action',
                sortable: false,
                align: 'end',
            },
        ]
    }

    get actions() {
        return [
            {
                type: 'commissionIt',
                label: i18n.t('actions.period.commissionIt.label'),
                disabled: this.billable || this.hasCommission,
                icon: 'mdi-cash-plus',
                hide: this.hasCommission,
                color: 'green',
            },
            {
                type: 'decommissionIt',
                label: i18n.t('actions.period.decommissionIt.label'),
                icon: 'mdi-cash-remove',
                hide: !this.hasCommission || this.billable,
                color: 'red',
            },
            {
                type: 'toNextCommission',
                label: i18n.t('actions.period.toNextCommission.label'),
                disabled: this.billable,
                icon: 'mdi-page-next',
            },
            {
                type: 'toPreviousCommission',
                label: i18n.t('actions.period.toPreviousCommission.label'),
                disabled: this.billable,
                icon: 'mdi-page-previous',
            },
        ]
    }

    get hasBill() {
        return this.bill instanceof Bill
    }

    get hasCommission() {
        return this.commission instanceof Commission
    }

    async commissionIt() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/commission-it`,
            method: 'PUT',
        })
    }

    async decommissionIt() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/decommission-it`,
            method: 'PUT',
        })
    }

    async toNextCommission() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/to-next-commission`,
            method: 'PUT',
        })
    }

    async toPreviousCommission() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/to-previous-commission`,
            method: 'PUT',
        })
    }
}
